import { resolvePublishRecordVideoUrl } from '@src/services/publish/publishRecordVideoUrl';
import { downloadVideo } from '@src/services/videoDownloadService';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { sanitizeFileName } from '@src/utility/filename';
import { formatLanguageCaption } from '@src/utility/languageCaption';
import { LogManager } from '@src/utility/Logger';
import { format } from 'date-fns';
import { useCallback, useRef, useState } from 'react';

import type { ClipDownloadJob } from '@src/services/publish/clipDownloadJobs';

const logger = LogManager.getLogger('content:useDownloadClips');

export type DownloadClipsResult = { downloaded: number; failed: number };

type UseDownloadClipsReturn = {
  downloadClips: (jobs: ClipDownloadJob[]) => Promise<DownloadClipsResult>;
  isDownloading: boolean;
};

/**
 * One file name per aspect ratio, so the variants of one clip don't collide:
 * `{story} - {clip} - {9x16} - {timestamp}`. The AR's ':' would be *stripped*
 * by `sanitizeFileName` (9:16 -> 916), so render the filename-safe dimension
 * form first — the same pre-step `buildPublishRecordFileName` makes. Sanitized
 * here because `downloadVideo` uses the name verbatim.
 *
 * A translated render takes its language between the two, so a folder of files
 * says which version each one is rather than presenting a dub as the original
 * (PLAT-11660). Omitted for an original-language publish, which keeps its name
 * byte-identical.
 */
function variantFileName(
  storyTitle: string | undefined,
  clipTitle: string | undefined,
  aspectRatio: string,
  language?: string,
): string {
  return sanitizeFileName(
    `${storyTitle || 'Untitled Story'} - ${clipTitle || 'Untitled Video'} - ${aspectRatio.replaceAll(
      ':',
      'x',
    )}${language ? ` - ${language}` : ''} - ${format(
      new Date(),
      'yyyy-MM-dd HH-mm-ss',
    )}`,
  );
}

/**
 * Downloads the published renders of the selected Video Clips (PLAT-11417) —
 * every aspect ratio each pick has, at its latest version.
 *
 * The caller hands over the file list (`clipDownloadJobs`) rather than the
 * clips, because the bulk bar decides whether to offer the action at all from
 * exactly that list: a gate that derived the set separately could offer a
 * Download this then found nothing for. A legacy job carries its URL already;
 * a translated one (whose render never reaches `videoFilePrimary`) resolves a
 * signed URL from its `PublishRecord`, the same path the Downloads tab and the
 * clip cards take.
 *
 * Files go one at a time: `downloadMedia` fetches each render whole into a blob
 * before handing it to the browser, so a parallel sweep would hold N full
 * videos in memory at once for no wall-clock gain worth the risk.
 */
export function useDownloadClips(): UseDownloadClipsReturn {
  const videoCreator = useVideoCreatorStore();

  const [isDownloading, setIsDownloading] = useState(false);
  const downloadingRef = useRef(false);

  const downloadClips = useCallback(
    async (jobs: ClipDownloadJob[]): Promise<DownloadClipsResult> => {
      if (downloadingRef.current) return { downloaded: 0, failed: 0 };

      const story = videoCreator.story;
      if (jobs.length === 0) return { downloaded: 0, failed: 0 };

      downloadingRef.current = true;
      setIsDownloading(true);

      const toastId = videoCreator.showToast({
        state: 'pending',
        message: `Downloading ${jobs.length} video${jobs.length === 1 ? '' : 's'}…`,
      });

      try {
        logger.info('downloadClips: starting', {
          fileCount: jobs.length,
          storyId: story?.id,
        });

        let downloaded = 0;
        for (const [index, job] of jobs.entries()) {
          if (jobs.length > 1) {
            videoCreator.updateToast(toastId, {
              state: 'pending',
              message: `Downloading ${index + 1} of ${jobs.length}…`,
            });
          }

          // A translated render's URL is signed per request, so it is resolved
          // here rather than when the job list was built — the list is rebuilt
          // on every render of the grid, and signing every candidate would
          // spend a request per selected variant on a Download nobody clicked.
          const url = job.record
            ? await resolvePublishRecordVideoUrl(job.record)
            : job.url;
          if (!url) {
            logger.error('downloadClips: no file for variant', {
              clipId: job.clip.id,
              aspectRatio: job.aspectRatio,
              recordId: job.record?.id,
            });
            continue;
          }

          // The shared service owns the fetch, the `download_clip` analytics
          // event, and the error logging; a failed variant only costs itself.
          const result = await downloadVideo({
            url,
            sanitizedFileName: variantFileName(
              story?.title,
              job.clip.title,
              job.aspectRatio,
              job.record
                ? formatLanguageCaption(job.record.language)
                : undefined,
            ),
            metadata: {
              clipId: job.clip.id,
              storyId: story?.id,
              aspectRatio: job.aspectRatio,
              sourcePlatform: 'content-studio',
            },
          });
          if (result.ok) downloaded += 1;
        }

        const failed = jobs.length - downloaded;
        videoCreator.updateToast(
          toastId,
          downloaded === 0
            ? {
                state: 'error',
                message: 'Could not download the videos. Please try again.',
              }
            : {
                state: failed > 0 ? 'warning' : 'success',
                message:
                  failed > 0
                    ? `Downloaded ${downloaded} of ${jobs.length} videos — ${failed} failed.`
                    : `Downloaded ${downloaded} video${downloaded === 1 ? '' : 's'}.`,
              },
        );
        logger.info('downloadClips: complete', { downloaded, failed });
        return { downloaded, failed };
      } finally {
        downloadingRef.current = false;
        setIsDownloading(false);
      }
    },
    [videoCreator],
  );

  return { downloadClips, isDownloading };
}
